import { useState, type KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { archHref, archLabel } from '@/help/architecture/tabs'
import {
  SYSTEM_EDGES,
  SYSTEM_ENVS,
  SYSTEM_NODES,
  edgeActive,
  findEnv,
  hasNode,
  neighbourhood,
  nodeFacts,
  nodeSubtitle,
  type EnvId,
  type NodeId,
} from '@/help/architecture/systemMap'
import { COLORS, SvgDefs, Zone, Badge } from './svg-primitives'
import { DiagramViewer } from './DiagramViewer'

const F = 'system-ui, -apple-system, sans-serif'
const ID = 'sys'

/** Where each node sits, in the 900 × 460 viewBox. */
const LAYOUT: Record<NodeId, { x: number; y: number; w: number; h: number }> = {
  ci: { x: 300, y: 20, w: 180, h: 56 },
  frontend: { x: 45, y: 185, w: 165, h: 70 },
  api: { x: 300, y: 185, w: 180, h: 70 },
  db: { x: 540, y: 180, w: 110, h: 80 },
  mock: { x: 60, y: 345, w: 180, h: 66 },
  mllp: { x: 330, y: 345, w: 190, h: 66 },
  partner: { x: 720, y: 348, w: 165, h: 60 },
}

/** Edge end points, keyed `from-to`. */
const EDGE_PATHS: Record<string, [number, number, number, number]> = {
  'frontend-api': [210, 220, 300, 220],
  'api-db': [480, 220, 540, 220],
  'api-mock': [340, 255, 170, 345],
  'api-mllp': [420, 255, 425, 345],
  'partner-mllp': [720, 378, 520, 378],
  'ci-api': [390, 76, 390, 185],
  'ci-frontend': [320, 76, 150, 185],
}

function NodeShape({ id, subtitle }: { id: NodeId; subtitle: string }) {
  const { x, y, w, h } = LAYOUT[id]
  const c = COLORS[id]
  const node = SYSTEM_NODES[id]
  const fill = `url(#${ID}-${id})`
  return (
    <>
      {id === 'db' ? (
        <path
          d={`M${x} ${y + 12} Q${x} ${y},${x + w / 2} ${y} Q${x + w} ${y},${x + w} ${y + 12} L${x + w} ${y + h - 12} Q${x + w} ${y + h},${x + w / 2} ${y + h} Q${x} ${y + h},${x} ${y + h - 12}Z`}
          fill={fill} stroke={c.stroke} strokeWidth={1.5} filter={`url(#${ID}-sh)`}
        />
      ) : (
        // Sharp corners mark an external actor, as the legend says.
        <rect x={x} y={y} width={w} height={h} rx={node.external ? 0 : 10}
          fill={fill} stroke={c.stroke} strokeWidth={1.5} filter={`url(#${ID}-sh)`} />
      )}
      <text x={x + w / 2} y={y + h / 2 - 7} textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={14} fontWeight={600} fill={c.text}>{node.title}</text>
      <text x={x + w / 2} y={y + h / 2 + 11} textAnchor='middle' dominantBaseline='middle'
        fontFamily={F} fontSize={11} fill={c.text} opacity={0.7}>{subtitle}</text>
    </>
  )
}

/**
 * The Architecture overview's system map (z8nrz7f3kc): one diagram, switched
 * between environments, whose components open a detail panel that links to
 * the sub-tab covering them.
 */
export function SystemMap() {
  const [envId, setEnvId] = useState<EnvId>('dev')
  const [selected, setSelected] = useState<NodeId | null>(null)
  const [hovered, setHovered] = useState<NodeId | null>(null)
  const env = findEnv(envId)

  const focus = hovered ?? selected
  const near = focus ? neighbourhood(focus) : null

  const onNodeKey = (e: KeyboardEvent, id: NodeId) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setSelected(s => (s === id ? null : id))
    }
  }

  const onEnvKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const i = SYSTEM_ENVS.findIndex(x => x.id === envId)
    const next = SYSTEM_ENVS[(i + (e.key === 'ArrowRight' ? 1 : SYSTEM_ENVS.length - 1)) % SYSTEM_ENVS.length]
    setEnvId(next.id)
    document.getElementById(`sys-env-${next.id}`)?.focus()
  }

  const controls = (
    <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
      <div role='radiogroup' aria-label='Environment' onKeyDown={onEnvKey} className='inline-flex w-fit flex-wrap rounded-lg border bg-muted/40 p-0.5'>
        {SYSTEM_ENVS.map(e => (
          <button
            key={e.id}
            id={`sys-env-${e.id}`}
            type='button'
            role='radio'
            aria-checked={e.id === envId}
            tabIndex={e.id === envId ? 0 : -1}
            onClick={() => setEnvId(e.id)}
            className={cn(
              'rounded-md px-2.5 py-1 font-mono text-xs transition-colors',
              e.id === envId ? 'bg-background font-semibold text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {e.label}
          </button>
        ))}
      </div>
      <div className='flex min-w-0 items-center gap-2 text-xs text-muted-foreground'>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            env.status === 'active' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
          )}
        >
          {env.status}
        </span>
        <span className='truncate font-mono'>{env.webDomain}</span>
      </div>
    </div>
  )

  const node = selected ? SYSTEM_NODES[selected] : null
  const panel = node ? (
    <div className='relative px-4 py-3 text-sm'>
      <button
        type='button'
        onClick={() => setSelected(null)}
        aria-label='Close details'
        title='Close (Esc)'
        className='absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground'
      >
        <X className='h-4 w-4' />
      </button>
      <div className='flex items-center gap-2 pr-8'>
        <span className='h-3 w-3 shrink-0 rounded-sm border' style={{ background: COLORS[node.id].bg, borderColor: COLORS[node.id].stroke }} aria-hidden='true' />
        <div className='font-semibold text-foreground'>{node.title}</div>
        {!hasNode(env, node.id) && (
          <span className='rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground'>
            not in {env.label}
          </span>
        )}
      </div>
      <div className='mt-1 text-muted-foreground'>{node.description}</div>
      <dl className='mt-3 grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-[auto_1fr]'>
        {nodeFacts(env, node.id).map(f => (
          <div key={f.label} className='contents'>
            <dt className='text-muted-foreground'>{f.label}</dt>
            <dd className='font-mono text-foreground'>{f.value}</dd>
          </div>
        ))}
      </dl>
      <Link
        to={archHref(node.tab)}
        className='mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline'
      >
        Read more in {archLabel(node.tab)}
        <ArrowRight className='h-3 w-3' />
      </Link>
    </div>
  ) : (
    <div className='px-4 py-3 text-sm text-muted-foreground'>
      Select a component to see what it is, where it runs and where to read more. Switch environment above to see how
      each stack differs.
    </div>
  )

  return (
    <div onKeyDown={e => { if (e.key === 'Escape') setSelected(null) }}>
      <DiagramViewer title='System Map' controls={controls} panel={<div aria-live='polite'>{panel}</div>}>
        <svg viewBox='0 0 900 460' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}
          role='group' aria-label={`System map, ${env.label} environment`}>
          <SvgDefs id={ID} />
          <Zone x={20} y={110} w={650} h={330} title={`${env.label.toUpperCase()} · ${env.apiDomain}`}
            bgColor='#f8fafc' borderColor='#cbd5e1' dashed={env.status !== 'active'} />
          {/* Right-aligned inside the zone; Badge sizes itself at ~6.5px per character. */}
          <Badge x={670 - 14 - (env.compute.length * 6.5 + 16) / 2} y={130} text={env.compute} color='#475569' />

          {SYSTEM_EDGES.map((e, i) => {
            const [x1, y1, x2, y2] = EDGE_PATHS[`${e.from}-${e.to}`]
            const live = edgeActive(env, e)
            const dim = !live || (near !== null && !near.edges.has(i))
            const hot = live && near !== null && near.edges.has(i)
            const mx = (x1 + x2) / 2
            const my = (y1 + y2) / 2
            const lw = e.label.length * 6.4 + 12
            return (
              <g key={i} style={{ opacity: dim ? (live ? 0.25 : 0.08) : 1, transition: 'opacity 0.2s' }}
                className={hot ? 'diagram-part-live' : undefined}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={hot ? '#7c3aed' : '#94a3b8'} strokeWidth={1.5}
                  strokeDasharray={e.dashed ? '6 4' : undefined} markerEnd={`url(#${ID}-arr)`} />
                <rect x={mx - lw / 2} y={my - 9} width={lw} height={18} rx={4} fill='white' stroke='#e2e8f0' strokeWidth={0.5} />
                <text x={mx} y={my + 1} textAnchor='middle' dominantBaseline='middle' fontFamily={F} fontSize={10} fill='#64748b'>
                  {e.label}
                </text>
              </g>
            )
          })}

          {(Object.keys(LAYOUT) as NodeId[]).map(id => {
            const present = hasNode(env, id)
            const dim = !present || (near !== null && !near.nodes.has(id))
            const { x, y, w, h } = LAYOUT[id]
            const isSel = selected === id
            return (
              <g
                key={id}
                role='button'
                tabIndex={0}
                aria-pressed={isSel}
                aria-label={`${SYSTEM_NODES[id].title}${present ? '' : `, not deployed in ${env.label}`}. Show details`}
                className='system-node cursor-pointer outline-none'
                style={{ opacity: dim ? (present ? 0.35 : 0.2) : 1, transition: 'opacity 0.2s' }}
                onClick={() => setSelected(s => (s === id ? null : id))}
                onKeyDown={e => onNodeKey(e, id)}
                onMouseEnter={() => setHovered(id)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(id)}
                onBlur={() => setHovered(null)}
              >
                <rect className='system-node-ring' x={x - 5} y={y - 5} width={w + 10} height={h + 10} rx={14}
                  fill='none' stroke='#7c3aed' strokeWidth={2} opacity={isSel ? 1 : 0} />
                <NodeShape id={id} subtitle={present ? nodeSubtitle(env, id) : `not in ${env.label}`} />
              </g>
            )
          })}
        </svg>
      </DiagramViewer>
    </div>
  )
}
